import React from 'react'
import { Dropdown } from 'react-bootstrap'
import { DocumentRequest } from '@trustero/trustero-api-web/lib/request/request_pb'
import { useNavigate } from 'react-router-dom'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  BasicDropdownMenu,
  BasicDropdownMenuItem,
  BasicDropdownSectionDivider,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import { DropdownToggleButton } from 'src/components/Reusable/Buttons'
import { ReactComponent as VerticalEllipsesIcon } from 'src/components/Icons/assets/vertical-ellipses-icon.svg'
import { useDeleteRequestModal } from 'src/pages/Requests/DeleteRequestModal/DeleteRequestModal'
import { useReceptors } from 'src/pages/Receptors/receptors.hooks'
import { ReceptorLogo } from 'src/components/async/model/receptor/ReceptorLogo'
import styled from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'

const WorkflowMenuLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const WorkflowMenuIcon = styled(ReceptorLogo)`
  width: 16px;
  height: 16px;
  object-fit: contain;
`

const RequestMenuItem = styled(BasicDropdownMenuItem)`
  width: 100%;
  justify-content: flex-start;
  text-align: left;

  &:hover,
  &:focus,
  &:active {
    color: ${palette.neutral[900]} !important;
  }
`

export const RequestItemMenu = ({
  request,
  permalink,
  onOpenCreateWorkflowTask,
}: {
  request: DocumentRequest
  permalink: string
  onOpenCreateWorkflowTask?: (
    request: DocumentRequest,
    receptorId: string,
  ) => void
}): JSX.Element => {
  const requestId = request.getId()
  const navigate = useNavigate()
  const canDeleteRequest = useCanPerformAction<DocumentRequest.AsObject>(
    CAPABILITY.CAN_DELETE_REQUESTS,
    request.toObject(),
  )
  const deleteRequest = useDeleteRequestModal({ request })
  const { data: jiraReceptorsRes } = useReceptors('trr-jira-workflow', true)
  const jiraWorkflowReceptors = (
    jiraReceptorsRes?.getReceptorsList() ?? []
  ).filter((r) => r.getIsenabled())
  const { data: snowReceptorsRes } = useReceptors(
    'trr-servicenow-workflow',
    true,
  )
  const snowWorkflowReceptors = (
    snowReceptorsRes?.getReceptorsList() ?? []
  ).filter((r) => r.getIsenabled())
  const workflowReceptors = [...jiraWorkflowReceptors, ...snowWorkflowReceptors]
  const hasWorkflowReceptor = workflowReceptors.length > 0
  const stopRowNavigation = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Dropdown
      id={`requests-index-menu-${requestId}`}
      onClick={stopRowNavigation}
    >
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <BasicDropdownMenu $offset={12}>
        <RequestMenuItem
          as="button"
          type="button"
          eventKey="1"
          onClick={(e: React.MouseEvent) => {
            stopRowNavigation(e)
            navigate(permalink)
          }}
        >
          View Details
        </RequestMenuItem>
        {canDeleteRequest && (
          <RequestMenuItem
            as="button"
            type="button"
            eventKey="3"
            onClick={(e: React.MouseEvent) => {
              stopRowNavigation(e)
              deleteRequest()
            }}
          >
            Delete Request
          </RequestMenuItem>
        )}
        {hasWorkflowReceptor && <BasicDropdownSectionDivider />}
        {hasWorkflowReceptor &&
          onOpenCreateWorkflowTask &&
          workflowReceptors.map((workflowReceptor, idx) => {
            const workflowModelId = workflowReceptor.getModelid() || ''
            const workflowInstanceName =
              workflowReceptor.getName()?.trim() || 'Workflow'
            return (
              <RequestMenuItem
                as="button"
                type="button"
                key={`workflow-ticket-${workflowReceptor.getId()}`}
                eventKey={`workflow-${idx}`}
                onClick={(e: React.MouseEvent) => {
                  stopRowNavigation(e)
                  onOpenCreateWorkflowTask(request, workflowReceptor.getId())
                }}
              >
                <WorkflowMenuLabel>
                  <WorkflowMenuIcon receptorModelId={workflowModelId} />
                  {`Create ${workflowInstanceName} Ticket`}
                </WorkflowMenuLabel>
              </RequestMenuItem>
            )
          })}
      </BasicDropdownMenu>
    </Dropdown>
  )
}
